import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Sparkles, X, Plus, ChevronDown, Search } from "lucide-react";
import api from "../../lib/api";
import { Card, Input, Localized } from "./Fields";
import ImageUpload from "./ImageUpload";

/* --- Small building blocks -------------------------------------------------- */

const CountedInput = ({ label, value = "", onChange, min = 40, max = 60, placeholder = "" }) => {
  const len = (value || "").length;
  const tone =
    len === 0
      ? "text-brand-medium"
      : len < min
      ? "text-amber-600"
      : len <= max
      ? "text-emerald-600"
      : "text-red-500";
  return (
    <div>
      <div className="flex items-center justify-between">
        <label className="block text-[0.68rem] tracking-[0.24em] uppercase text-brand-medium mb-1.5">
          {label}
        </label>
        <span className={`text-[0.68rem] font-medium ${tone}`}>
          {len}/{max}
        </span>
      </div>
      <input
        type="text"
        className="w-full bg-transparent border-b border-black/15 focus:border-brand-navy focus:outline-none py-2 text-brand-dark text-sm placeholder-brand-medium/60"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
};

const CountedTextarea = ({ label, value = "", onChange, min = 140, max = 160, rows = 3 }) => {
  const len = (value || "").length;
  const tone =
    len === 0
      ? "text-brand-medium"
      : len < min
      ? "text-amber-600"
      : len <= max
      ? "text-emerald-600"
      : "text-red-500";
  return (
    <div>
      <div className="flex items-center justify-between">
        <label className="block text-[0.68rem] tracking-[0.24em] uppercase text-brand-medium mb-1.5">
          {label}
        </label>
        <span className={`text-[0.68rem] font-medium ${tone}`}>
          {len}/{max}
        </span>
      </div>
      <textarea
        rows={rows}
        className="w-full bg-transparent border border-black/15 focus:border-brand-navy focus:outline-none py-2 px-3 text-brand-dark text-sm resize-y"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
};

/* --- Google SERP preview mockup -------------------------------------------- */

const SerpPreview = ({ title, description, slug = "" }) => {
  const displayTitle = title || "Sayfa Başlığı — Fırat Construction";
  const displayDesc =
    description ||
    "Meta açıklama boş. Doldurduğunuzda Google arama sonuçlarında burada görünecek.";
  const displayUrl = `https://www.firatconstruction.com${slug ? "/" + slug.replace(/^\//, "") : ""}`;
  return (
    <div className="border border-black/10 bg-white p-4">
      <div className="text-[0.62rem] tracking-[0.28em] uppercase text-brand-medium mb-3 flex items-center gap-2">
        <Search size={12} strokeWidth={1.5} />
        Google Önizleme
      </div>
      <div className="text-[0.75rem] text-[#4d5156] mb-1">{displayUrl}</div>
      <div className="text-[1.05rem] leading-snug text-[#1a0dab] font-medium hover:underline cursor-default line-clamp-2">
        {displayTitle}
      </div>
      <div className="text-[0.82rem] text-[#4d5156] leading-snug mt-1 line-clamp-2">
        {displayDesc}
      </div>
    </div>
  );
};

/* --- H2/H3 array editor ---------------------------------------------------- */

const ArrayLocalized = ({ label, value = [], onChange, hint = "" }) => (
  <div>
    <div className="flex items-center justify-between mb-3">
      <label className="text-[0.68rem] tracking-[0.24em] uppercase text-brand-medium">
        {label}
      </label>
      <button
        type="button"
        onClick={() => onChange([...(value || []), { tr: "", en: "", ar: "" }])}
        className="inline-flex items-center gap-1.5 text-[0.65rem] tracking-[0.2em] uppercase text-brand-primary hover:text-brand-navy transition-colors"
      >
        <Plus size={12} strokeWidth={1.5} /> Ekle
      </button>
    </div>
    {hint ? <p className="text-xs text-brand-medium mb-3">{hint}</p> : null}
    <div className="space-y-3">
      {(value || []).map((item, i) => (
        <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2 items-start">
          <Localized
            value={item}
            onChange={(v) => {
              const next = [...(value || [])];
              next[i] = v;
              onChange(next);
            }}
          />
          <button
            type="button"
            onClick={() => onChange((value || []).filter((_, k) => k !== i))}
            className="mt-1 p-2 border border-red-200 text-red-500 hover:bg-red-50"
            aria-label="Sil"
          >
            <X size={12} />
          </button>
        </div>
      ))}
      {!(value || []).length ? (
        <div className="text-xs text-brand-medium italic">Henüz eklenmedi.</div>
      ) : null}
    </div>
  </div>
);

/* --- Main SEO editor -------------------------------------------------------- */

/**
 * SEOFields — comprehensive SEO panel with AI Suggest.
 * Props:
 *   value: SEO object
 *   onChange: (nextSeo) => void
 *   context: { title, content, category, slug } — used for AI suggestions
 *   defaultOpen: bool — expand collapsible by default
 */
const SEOFields = ({ value, onChange, context = {}, defaultOpen = true }) => {
  const v = value || {};
  const set = (k, val) => onChange({ ...v, [k]: val });
  const setLoc = (k, langCode, val) =>
    set(k, { ...(v[k] || {}), [langCode]: val });

  const [open, setOpen] = useState(defaultOpen);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiLang, setAiLang] = useState("tr");

  const langs = useMemo(() => ["tr", "en", "ar"], []);

  const applyAiSuggestion = (lang, s) => {
    const next = { ...v };
    next.focus_keyword = s.focus_keyword || next.focus_keyword;
    next.title = { ...(next.title || {}), [lang]: s.meta_title || "" };
    next.description = { ...(next.description || {}), [lang]: s.meta_description || "" };
    next.keywords = Array.isArray(s.keywords) ? s.keywords.join(", ") : (s.keywords || next.keywords);
    next.h1 = { ...(next.h1 || {}), [lang]: s.h1 || "" };
    next.h2 = (s.h2 || []).map((h, i) => ({ ...(next.h2?.[i] || { tr: "", en: "", ar: "" }), [lang]: h }));
    next.h3 = (s.h3 || []).map((h, i) => ({ ...(next.h3?.[i] || { tr: "", en: "", ar: "" }), [lang]: h }));
    next.og_title = { ...(next.og_title || {}), [lang]: s.og_title || "" };
    next.og_description = { ...(next.og_description || {}), [lang]: s.og_description || "" };
    if (s.schema_type) next.schema_type = s.schema_type;
    onChange(next);
  };

  const runAi = async () => {
    const title = context.title || v.h1?.[aiLang] || v.title?.[aiLang] || "";
    const content = context.content || "";
    if (!title && !content) {
      toast.error("Önce sayfa başlığı ve içerik girin — sonra AI önerisi alın.");
      return;
    }
    setAiBusy(true);
    try {
      const { data } = await api.post("/admin/seo/suggest", {
        title,
        content,
        category: context.category || "page",
        lang: aiLang,
      });
      applyAiSuggestion(aiLang, data.suggestions || {});
      toast.success(`AI SEO önerileri uygulandı (${aiLang.toUpperCase()})`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "AI hatası");
    } finally {
      setAiBusy(false);
    }
  };

  /* Header row shown even when the card is collapsed */
  const header = (
    <button
      type="button"
      onClick={() => setOpen((x) => !x)}
      className="w-full flex items-center justify-between text-left"
    >
      <div className="flex items-center gap-3">
        <span className="w-9 h-9 rounded-full bg-brand-primary/10 text-brand-primary flex items-center justify-center">
          <Search size={16} strokeWidth={1.5} />
        </span>
        <div>
          <div className="text-[0.68rem] tracking-[0.28em] uppercase text-brand-primary font-medium">
            SEO & Arama Motoru Optimizasyonu
          </div>
          <div className="text-brand-medium text-xs mt-0.5">
            Google&apos;da ilk sırada çıkmak için sayfa başlıkları, meta açıklaması, anahtar kelimeler ve yapay zeka önerileri.
          </div>
        </div>
      </div>
      <ChevronDown
        size={18}
        strokeWidth={1.5}
        className={`text-brand-medium transition-transform duration-300 ${open ? "rotate-180" : ""}`}
      />
    </button>
  );

  return (
    <div className="border border-brand-primary/25 bg-gradient-to-br from-brand-primary/[0.03] to-transparent">
      <div className="p-5 md:p-6 border-b border-brand-primary/15">
        {header}
      </div>

      {open ? (
        <div className="p-5 md:p-6 space-y-8">
          {/* AI Suggest banner */}
          <div className="bg-white border border-brand-primary/20 p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex items-center gap-3 flex-1">
              <span className="w-10 h-10 rounded-full bg-brand-primary text-white flex items-center justify-center">
                <Sparkles size={16} strokeWidth={1.5} />
              </span>
              <div className="min-w-0">
                <div className="font-serif text-brand-navy text-base md:text-lg leading-tight">
                  AI SEO Asistanı
                </div>
                <div className="text-xs text-brand-medium mt-0.5">
                  Sayfa başlığı ve içeriğine göre profesyonel SEO metadatasını otomatik oluşturur.
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <select
                value={aiLang}
                onChange={(e) => setAiLang(e.target.value)}
                className="border border-black/15 bg-white text-xs px-3 py-2.5 focus:border-brand-navy focus:outline-none"
                disabled={aiBusy}
              >
                <option value="tr">Türkçe</option>
                <option value="en">English</option>
                <option value="ar">العربية</option>
              </select>
              <button
                type="button"
                onClick={runAi}
                disabled={aiBusy}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-navy text-white text-[0.7rem] tracking-[0.22em] uppercase font-medium hover:bg-brand-primary transition-colors disabled:opacity-60"
                data-testid="seo-ai-suggest"
              >
                <Sparkles size={12} strokeWidth={1.5} />
                {aiBusy ? "Analiz ediliyor…" : "Öneri Al"}
              </button>
            </div>
          </div>

          {/* Focus keyword */}
          <div>
            <label className="block text-[0.68rem] tracking-[0.24em] uppercase text-brand-medium mb-1.5">
              Odak Anahtar Kelime
            </label>
            <p className="text-xs text-brand-medium mb-2">
              Bu sayfanın Google&apos;da hedeflediği ana kelime öbeği (2-5 kelime).
            </p>
            <input
              type="text"
              placeholder="Ör: boğaz manzaralı lüks konut"
              className="w-full bg-transparent border-b border-black/15 focus:border-brand-navy focus:outline-none py-2 text-brand-dark text-sm"
              value={v.focus_keyword || ""}
              onChange={(e) => set("focus_keyword", e.target.value)}
              data-testid="seo-focus"
            />
          </div>

          {/* Meta title / description per language */}
          <div>
            <div className="text-[0.68rem] tracking-[0.28em] uppercase text-brand-navy font-medium mb-3">
              Meta Başlık & Açıklama
            </div>
            <div className="space-y-6">
              {langs.map((code) => (
                <div key={code} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <CountedInput
                    label={`Meta Başlık (${code.toUpperCase()})`}
                    value={v.title?.[code]}
                    onChange={(val) => setLoc("title", code, val)}
                    min={40}
                    max={60}
                    placeholder="Ör: Boğaz Manzaralı Lüks Konut | Fırat"
                  />
                  <CountedTextarea
                    label={`Meta Açıklama (${code.toUpperCase()})`}
                    value={v.description?.[code]}
                    onChange={(val) => setLoc("description", code, val)}
                    min={140}
                    max={160}
                    rows={2}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* SERP preview */}
          <SerpPreview title={v.title?.tr} description={v.description?.tr} slug={context.slug} />

          {/* Keywords + canonical + schema */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Anahtar Kelimeler (virgülle ayır)"
              value={v.keywords || ""}
              onChange={(val) => set("keywords", val)}
            />
            <Input
              label="Canonical URL"
              value={v.canonical || ""}
              onChange={(val) => set("canonical", val)}
              placeholder="https://www.firatconstruction.com/..."
            />
            <div>
              <label className="block text-[0.68rem] tracking-[0.24em] uppercase text-brand-medium mb-1.5">
                Schema.org Türü
              </label>
              <select
                value={v.schema_type || "WebPage"}
                onChange={(e) => set("schema_type", e.target.value)}
                className="w-full bg-white border border-black/15 focus:border-brand-navy focus:outline-none py-2.5 px-3 text-sm"
              >
                <option value="WebPage">WebPage — Genel sayfa</option>
                <option value="Article">Article — Haber / makale</option>
                <option value="Organization">Organization — Şirket</option>
                <option value="RealEstateListing">RealEstateListing — Gayrimenkul ilanı</option>
                <option value="LocalBusiness">LocalBusiness — Yerel işletme</option>
                <option value="JobPosting">JobPosting — İş ilanı</option>
              </select>
            </div>
            <div>
              <label className="block text-[0.68rem] tracking-[0.24em] uppercase text-brand-medium mb-1.5">
                Twitter Card
              </label>
              <select
                value={v.twitter_card || "summary_large_image"}
                onChange={(e) => set("twitter_card", e.target.value)}
                className="w-full bg-white border border-black/15 focus:border-brand-navy focus:outline-none py-2.5 px-3 text-sm"
              >
                <option value="summary_large_image">summary_large_image (önerilen)</option>
                <option value="summary">summary</option>
              </select>
            </div>
          </div>

          {/* Open Graph */}
          <div>
            <div className="text-[0.68rem] tracking-[0.28em] uppercase text-brand-navy font-medium mb-3">
              Sosyal Medya (Open Graph)
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                <Localized
                  label="OG Başlık (paylaşıldığında görünen başlık)"
                  value={v.og_title || {}}
                  onChange={(val) => set("og_title", val)}
                />
                <Localized
                  label="OG Açıklama"
                  value={v.og_description || {}}
                  onChange={(val) => set("og_description", val)}
                  textarea
                />
              </div>
              <div>
                <ImageUpload
                  label="OG Görseli (1200×630, sosyal paylaşımlarda görünür)"
                  value={v.og_image || ""}
                  onChange={(val) => set("og_image", val)}
                />
                <p className="text-xs text-brand-medium mt-2">
                  Facebook, LinkedIn, Twitter&apos;da sayfa paylaşıldığında bu görsel gösterilir.
                </p>
              </div>
            </div>
          </div>

          {/* Headings */}
          <div>
            <div className="text-[0.68rem] tracking-[0.28em] uppercase text-brand-navy font-medium mb-3">
              Sayfa Başlıkları (H1 → H3)
            </div>
            <div className="space-y-6">
              <Localized
                label="H1 — Ana Başlık (yalnızca 1 adet)"
                value={v.h1 || {}}
                onChange={(val) => set("h1", val)}
              />
              <ArrayLocalized
                label="H2 — Bölüm Başlıkları"
                value={v.h2 || []}
                onChange={(val) => set("h2", val)}
                hint="Sayfayı bölümlere ayıran alt başlıklar. Focus keyword'ün varyantlarını içermeli."
              />
              <ArrayLocalized
                label="H3 — Alt Bölüm Başlıkları"
                value={v.h3 || []}
                onChange={(val) => set("h3", val)}
                hint="Her bir bölümün içindeki alt başlıklar."
              />
            </div>
          </div>

          {/* Index controls */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="flex items-center gap-3 cursor-pointer p-3 border border-black/10 hover:border-brand-navy transition-colors">
              <input
                type="checkbox"
                checked={!!v.noindex}
                onChange={(e) => set("noindex", e.target.checked)}
              />
              <div className="min-w-0">
                <div className="text-sm text-brand-navy font-medium">Arama motorlarında gizle</div>
                <div className="text-xs text-brand-medium mt-0.5">noindex — Google bu sayfayı listelemez</div>
              </div>
            </label>
            <label className="flex items-center gap-3 cursor-pointer p-3 border border-black/10 hover:border-brand-navy transition-colors">
              <input
                type="checkbox"
                checked={!!v.nofollow}
                onChange={(e) => set("nofollow", e.target.checked)}
              />
              <div className="min-w-0">
                <div className="text-sm text-brand-navy font-medium">Bağlantıları takip etme</div>
                <div className="text-xs text-brand-medium mt-0.5">nofollow — Sayfadaki linklerin SEO değeri geçmez</div>
              </div>
            </label>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default SEOFields;
